import { Link } from "react-router-dom";
import { Heart, MapPin, Truck, ShieldCheck, Crown, Flame } from "lucide-react";
import { useState } from "react";
import { fmtPrice, isFavorite, toggleFavorite, withWatermark } from "@/lib/utils";
import { toggleFavoriteRemote } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { logger } from "@/lib/logger";

const YANGO_ELIGIBLE = ["Electronique", "Mobiliers", "Materiel pro", "Loisir", "Mode", "Montres & Bijoux", "Accessoires & Bagagerie"];

export default function AnnonceCard({ a, compact = false }) {
  const { user } = useAuth();
  const [fav, setFav] = useState(isFavorite(a.id));

  const handleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(a.id);
    setFav((f) => !f);
    if (user) {
      try { await toggleFavoriteRemote(a.id); }
      catch (e) { logger.warn("Favorite sync failed", e); }
    }
  };

  return (
    <Link to={`/annonces/${a.id}`} className="block" data-testid={`annonce-card-${a.id}`}>
      <div className="card relative h-full flex flex-col">
        <div className="relative">
          <img
            src={withWatermark(a.image)}
            alt={a.title}
            className="w-full h-44 object-cover object-bottom"
            loading="lazy"
            onError={(e) => { e.target.onerror = null; e.target.src = withWatermark("https://images.unsplash.com/photo-1481277542470-605612bd2d61?auto=format&fit=crop&w=900&q=80"); }}
          />
          {a.tag && <span className="tag" data-testid={`annonce-tag-${a.id}`}>{a.tag}</span>}
          <div className="absolute bottom-2 left-2 flex gap-1">
            {a.is_sold && (
              <span className="inline-flex items-center gap-1 bg-emerald-600 text-white text-[10px] font-extrabold px-2 py-1 rounded-full shadow-md" data-testid={`sold-badge-${a.id}`}>VENDU</span>
            )}
            {a.is_boosted && (
              <span className="inline-flex items-center gap-1 bg-gradient-to-r from-rose-500 to-orange-500 text-white text-[10px] font-extrabold px-2 py-1 rounded-full shadow-md" title="Annonce boostee" data-testid={`boost-badge-${a.id}`}>
                <Flame size={11} /> A LA UNE
              </span>
            )}
            {a.owner_is_pro && (
              <span
                className="inline-flex items-center gap-1 bg-gradient-to-r from-amber-400 to-yellow-500 text-amber-950 text-[10px] font-extrabold px-2 py-1 rounded-full shadow-md"
                title="Vendeur abonné Fodindin Pro"
                data-testid={`pro-badge-${a.id}`}
              >
                <Crown size={11} /> PRO
              </span>
            )}
            {a.seller_verified && (
              <span
                className="inline-flex items-center gap-1 bg-emerald-600 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-md"
                title="Vendeur dont le numéro de téléphone a été vérifié par SMS"
                data-testid={`seller-verified-${a.id}`}
              >
                <ShieldCheck size={11} /> Vendeur vérifié
              </span>
            )}
          </div>
          <button
            className={`heart-btn ${fav ? "fav" : ""}`}
            onClick={handleFav}
            aria-label="Ajouter aux favoris"
            data-testid={`annonce-fav-${a.id}`}
          >
            <Heart size={17} fill={fav ? "#ef4444" : "none"} />
          </button>
        </div>
        <div className="p-4 flex-1 flex flex-col">
          {a.ref ? (
            <div className="text-[10px] font-bold tracking-wider text-orange-600 mb-1" data-testid={`card-ref-${a.id}`}>
              {a.ref}
            </div>
          ) : null}
          <h3 className="font-bold text-[15px] leading-snug text-[var(--ink)] line-clamp-2 min-h-[40px]">
            {a.title}
          </h3>
          <div className="mt-2 text-orange-600 font-bold text-[15px]">
            {fmtPrice(a.price, a.price_unit)}
          </div>
          {!compact && (
            <div className="mt-2 flex items-center gap-1 text-xs text-[var(--ink-soft)]">
              <MapPin size={12} />
              <span>{a.category} - {a.city}{a.quartier ? `, ${a.quartier}` : ""}</span>
            </div>
          )}
          {YANGO_ELIGIBLE.includes(a.category) && (
            <div
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-orange-700 bg-orange-50 border border-orange-200 px-2 py-1 rounded-full self-start"
              data-testid={`yango-eligible-${a.id}`}
            >
              <Truck size={11} /> Livraison Yango
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
